import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { Protected } from '../components/Guard';
import { Toaster } from '../components/Toast';
import { useEffect } from 'react';
import { setLanguage, useI18n } from '../i18n';
import { useLocale } from '../lib/prefs';
import { useMe } from '../lib/queries';
import { SignInPage, Welcome } from '../features/auth/Welcome';
import { DemoPage } from '../features/canvas/DemoPage';
import { TreePage } from '../features/canvas/TreePage';
import { Birthdays } from '../features/birthdays/Birthdays';
import { History } from '../features/history/History';
import { DeleteAccountInfo, Privacy, Terms } from '../features/settings/Static';
import { ConsentBanner } from '../components/Legal';
import { Settings } from '../features/settings/Settings';
import { InviteLanding } from '../features/trees/InviteLanding';
import { Members } from '../features/trees/Members';
import { TreeList } from '../features/trees/TreeList';
import { ViewsPage } from '../features/views/ViewsPage';

/** Loads the account when signed in, so its language choices reach every page (lib/prefs). */
function PrefsSync() {
  useMe();
  const locale = useLocale();
  useEffect(() => {
    if (locale !== useI18n.getState().locale) void setLanguage(locale);
  }, [locale]);
  return null;
}

const root = createRootRoute({ component: () => (<><Outlet /><Toaster /><ConsentBanner /><PrefsSync /></>) });
const r = (path: string, component: () => React.ReactNode, extra: object = {}) => createRoute({ getParentRoute: () => root, path, component, ...extra } as never);

const index = r('/', () => <Welcome />);
const signIn = createRoute({
  getParentRoute: () => root,
  path: '/sign-in',
  validateSearch: (s: Record<string, unknown>) => ({ redirect: typeof s.redirect === 'string' ? s.redirect : undefined }),
  component: function SignInRoute() {
    const { redirect } = signIn.useSearch();
    return <SignInPage redirect={redirect} />;
  },
});
const invite = createRoute({ getParentRoute: () => root, path: '/i/$token', component: function InviteRoute() { return <InviteLanding token={invite.useParams().token} />; } });
const trees = r('/trees', () => <Protected><TreeList /></Protected>);
const tree = createRoute({ getParentRoute: () => root, path: '/t/$treeId', component: function TreeRoute() { return <Protected><TreePage treeId={tree.useParams().treeId} /></Protected>; } });
const members = createRoute({ getParentRoute: () => root, path: '/t/$treeId/members', component: function MembersRoute() { return <Protected><Members treeId={members.useParams().treeId} /></Protected>; } });
const history = createRoute({ getParentRoute: () => root, path: '/t/$treeId/history', component: function HistoryRoute() { return <Protected><History treeId={history.useParams().treeId} /></Protected>; } });
const birthdays = createRoute({ getParentRoute: () => root, path: '/t/$treeId/birthdays', component: function BirthdaysRoute() { return <Protected><Birthdays treeId={birthdays.useParams().treeId} /></Protected>; } });
const views = createRoute({ getParentRoute: () => root, path: '/t/$treeId/list', component: function ViewsRoute() { return <Protected><ViewsPage treeId={views.useParams().treeId} /></Protected>; } });
const settings = r('/settings', () => <Protected><Settings /></Protected>);
const settingsSection = createRoute({ getParentRoute: () => root, path: '/settings/$section', component: function SettingsSectionRoute() { return <Protected><Settings section={settingsSection.useParams().section} /></Protected>; } });
const privacy = r('/privacy', () => <Privacy />);
const terms = r('/terms', () => <Terms />);
const deleteInfo = r('/delete-account', () => <DeleteAccountInfo />);
const demo = r('/demo', () => <DemoPage />);

const routeTree = root.addChildren([index, signIn, invite, trees, tree, members, history, birthdays, views, settings, settingsSection, privacy, terms, deleteInfo, demo]);
export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
